/**
 * Admin top bar (minimal counterpart of web/src/components/navigation/TopBar.tsx).
 *
 * Shows the product name, the operator role badge resolved by RequireRole
 * (useAdminRole()), and the sign-out control.
 */
import { NavLink, useParams } from 'react-router-dom';
import { useClerk } from '@clerk/clerk-react';
import { getAdminNav } from '@/config/navigation';
import { useAdminRole } from '@/auth/AdminRoleContext';
import { BrandMark } from './BrandMark';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { LogOut } from '@/components/icons';
import { cn } from '@/lib/utils';

export function AdminTopBar() {
  const { role } = useAdminRole();
  const { signOut } = useClerk();
  // Same derived nav as the sidebar: role + the open-account route param.
  const { accountID } = useParams<{ accountID: string }>();
  const items = getAdminNav({ role, openAccountID: accountID });

  return (
    <header className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b bg-surface/95 px-4 backdrop-blur supports-[backdrop-filter]:bg-surface/80">
      {/* Compact brand + inline navigation for screens without the sidebar. */}
      <div className="md:hidden">
        <BrandMark withWordmark={false} />
      </div>
      <nav aria-label="Admin navigation" className="flex items-center gap-1 md:hidden">
        {items.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === '/'}
            className={({ isActive }) =>
              cn(
                'rounded-md px-2 py-1 text-xs font-medium transition-colors',
                isActive ? 'text-primary' : 'text-muted-foreground',
              )
            }
          >
            {item.label}
          </NavLink>
        ))}
      </nav>
      <div className="flex-1" />
      <Badge variant={role === 'admin' ? 'info' : 'secondary'} title="Operator role">
        {role === 'admin' ? 'Admin (read/write)' : 'Support (read-only)'}
      </Badge>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => {
          void signOut({ redirectUrl: '/sign-in' });
        }}
      >
        <LogOut aria-hidden="true" />
        Sign out
      </Button>
    </header>
  );
}
